import { useEffect, useRef, useState } from "react";
import {
  MapContainer, TileLayer, Marker,
  Polyline, CircleMarker, useMap,
} from "react-leaflet";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Clock, Navigation, Plus, Minus, Maximize2 } from "lucide-react";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl:       require("leaflet/dist/images/marker-icon.png"),
  shadowUrl:     require("leaflet/dist/images/marker-shadow.png"),
});

const INDIA_CENTER = [22.5, 80.0];
const INDIA_BOUNDS = L.latLngBounds(L.latLng(6.5, 68.0), L.latLng(37.5, 97.5));
const STATUS_COLOR = { Active:"#16A34A", Idle:"#D97706", Offline:"#9CA3AF" };


/* ── Vehicle marker ────────────────────────────────────── */
function makeIcon(vehicle, isSelected) {
  const color = STATUS_COLOR[vehicle.status] || "#9CA3AF";
  const plate = vehicle.plateNumber || "";
  const tag   = plate.length > 4 ? plate.slice(-4) : plate;

  // Truck SVG icon (side-view silhouette)
  const truckSvg = `<svg width="20" height="14" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="4" width="20" height="12" rx="2" fill="#fff"/>
    <path d="M20 7h7l3 5v4h-10V7z" fill="#fff"/>
    <circle cx="6" cy="17" r="3" fill="#fff" stroke="${color}" stroke-width="1.5"/>
    <circle cx="24" cy="17" r="3" fill="#fff" stroke="${color}" stroke-width="1.5"/>
  </svg>`;

  if (isSelected) {
    // Glow pulse behind the pin (no rings, just a soft shadow bloom)
    const glowAnim = vehicle.isTracking
      ? `<style>@keyframes glow{0%,100%{box-shadow:0 0 0 0 ${color}55,0 4px 16px ${color}44}50%{box-shadow:0 0 0 10px ${color}00,0 4px 20px ${color}66}}</style>`
      : "";
    const glowStyle = vehicle.isTracking ? "animation:glow 2s ease-in-out infinite;" : "";

    return L.divIcon({
      className:"", iconSize:[72,76], iconAnchor:[36,76],
      html:`${glowAnim}
      <div style="width:72px;height:76px;display:flex;flex-direction:column;align-items:center;cursor:pointer;">
        <!-- Teardrop pin body -->
        <div style="
          width:44px;height:44px;border-radius:50% 50% 50% 4px;
          transform:rotate(-45deg);
          background:${color};
          box-shadow:0 4px 18px ${color}66,0 2px 8px rgba(0,0,0,0.22);
          ${glowStyle}
          display:flex;align-items:center;justify-content:center;
          flex-shrink:0;
          border:2.5px solid #ffffff;
        ">
          <!-- Inner icon — counter-rotate so it stays upright -->
          <div style="transform:rotate(45deg);display:flex;align-items:center;justify-content:center;">
            ${truckSvg}
          </div>
        </div>
        <!-- Plate tag -->
        <div style="
          background:#1E293B;color:#fff;
          font-size:11px;font-weight:800;font-family:system-ui;
          padding:3px 9px;border-radius:5px;margin-top:6px;
          white-space:nowrap;letter-spacing:0.6px;
          box-shadow:0 2px 8px rgba(0,0,0,0.25);
        ">${tag}</div>
      </div>`,
    });
  }

  // Unselected: small solid diamond + plate label
  return L.divIcon({
    className:"", iconSize:[52,46], iconAnchor:[26,10],
    html:`<div style="display:flex;flex-direction:column;align-items:center;width:52px;cursor:pointer;">
      <div style="
        width:14px;height:14px;
        background:${color};
        border:2px solid #ffffff;
        border-radius:3px;
        transform:rotate(45deg);
        box-shadow:0 1px 5px rgba(0,0,0,0.28);
        flex-shrink:0;
      "></div>
      <div style="
        background:${color === "#9CA3AF" ? "#4B5563" : color};color:#fff;
        font-size:10px;font-weight:700;font-family:system-ui;
        padding:2px 7px;border-radius:4px;margin-top:5px;
        white-space:nowrap;letter-spacing:0.4px;
      ">${tag}</div>
    </div>`,
  });
}

/* ── Home pin (RC registered address) ─────────────────── */
function makeHomeIcon(isSelected) {
  const border = isSelected ? "#2563EB" : "#9CA3AF";
  const bg     = isSelected ? "#EEF2FF" : "#F3F4F6";
  return L.divIcon({
    className:"", iconSize:[36,36], iconAnchor:[18,18],
    html:`<div style="
      width:36px;height:36px;border-radius:10px;
      background:${bg};border:2px solid ${border};
      display:flex;align-items:center;justify-content:center;
      font-size:18px;cursor:pointer;
      box-shadow:0 2px 8px rgba(0,0,0,0.15);
    ">🏠</div>`,
  });
}

/* ── State-estimate pin (free geocode from plate RTO) ──── */
function makeStateIcon(isSelected) {
  const border = isSelected ? "#2563EB" : "#D97706";
  const bg     = isSelected ? "#EEF2FF" : "#FFFBEB";
  const color  = isSelected ? "#2563EB" : "#D97706";
  return L.divIcon({
    className:"", iconSize:[36,36], iconAnchor:[18,18],
    html:`<div style="
      width:36px;height:36px;border-radius:10px;
      background:${bg};border:2px dashed ${border};
      display:flex;align-items:center;justify-content:center;
      font-size:15px;font-weight:700;color:${color};
      cursor:pointer;font-family:system-ui;
      box-shadow:0 2px 8px rgba(0,0,0,0.12);
    ">~</div>`,
  });
}

/* ── Toll icon ─────────────────────────────────────────── */
function makeTollIcon(isActive) {
  if (isActive) return L.divIcon({
    className:"", iconSize:[30,30], iconAnchor:[15,15],
    html:`<div style="
      width:30px;height:30px;border-radius:50%;background:#7C3AED;
      border:2.5px solid #ffffff;display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:700;color:#fff;font-family:system-ui;
      box-shadow:0 4px 12px rgba(124,58,237,0.45);
    ">₹</div>`,
  });
  return L.divIcon({
    className:"", iconSize:[22,22], iconAnchor:[11,11],
    html:`<div style="
      width:22px;height:22px;border-radius:50%;background:#fff;
      border:1.5px solid #7C3AED;display:flex;align-items:center;justify-content:center;
      font-size:10px;font-weight:700;color:#7C3AED;font-family:system-ui;cursor:pointer;
      box-shadow:0 1px 4px rgba(0,0,0,0.12);
    ">₹</div>`,
  });
}

/* ── Start marker ──────────────────────────────────────── */
const START_ICON = L.divIcon({
  className:"", iconSize:[86,29], iconAnchor:[8,14],
  html:`<div style="display:flex;align-items:center;gap:7px;">
    <div style="
      width:17px;height:17px;border-radius:50%;flex-shrink:0;
      background:#16A34A;border:3px solid #fff;
      box-shadow:0 0 0 2px #16A34A,0 2px 8px rgba(22,163,74,0.5);
    "></div>
    <div style="
      background:#16A34A;color:#fff;
      font-size:11px;font-weight:800;font-family:system-ui;
      padding:3px 8px;border-radius:5px;white-space:nowrap;
      box-shadow:0 2px 8px rgba(22,163,74,0.45);letter-spacing:0.4px;
    ">START</div>
  </div>`,
});

/* ── Last-known location marker ────────────────────────── */
const CURRENT_ICON = L.divIcon({
  className:"", iconSize:[103,29], iconAnchor:[8,14],
  html:`<div style="display:flex;align-items:center;gap:7px;">
    <div style="
      width:17px;height:17px;border-radius:50%;flex-shrink:0;
      background:#2563EB;border:3px solid #fff;
      box-shadow:0 0 0 2px #2563EB,0 2px 8px rgba(79,70,229,0.5);
    "></div>
    <div style="
      background:#2563EB;color:#fff;
      font-size:11px;font-weight:800;font-family:system-ui;
      padding:3px 8px;border-radius:5px;white-space:nowrap;
      box-shadow:0 2px 8px rgba(79,70,229,0.45);letter-spacing:0.4px;
    ">LAST SEEN</div>
  </div>`,
});

/* ── Toll markers ──────────────────────────────────────── */
function TollMarkers({ vehicle, activeTollId, onTollClick, onTollHover }) {
  if (!vehicle?.tollHistory?.length) return null;
  const plazas = vehicle.tollHistory.filter(t => t.lat && t.lng);
  if (!plazas.length) return null;
  return plazas.map(toll => (
    <Marker
      key={toll.id}
      position={[toll.lat, toll.lng]}
      icon={makeTollIcon(toll.id === activeTollId)}
      zIndexOffset={600}
      eventHandlers={{
        click:     () => onTollClick(toll),
        mouseover: e  => onTollHover(toll, e.originalEvent),
        mouseout:  ()  => onTollHover(null),
      }}
    />
  ));
}

/* ── Toll detail card ──────────────────────────────────── */
function TollDetailCard({ toll, onClose }) {
  const ts = toll.timestamp
    ? new Date(toll.timestamp).toLocaleString("en-IN", {
        day:"numeric", month:"short", hour:"2-digit", minute:"2-digit", hour12:true,
      })
    : null;

  const dir = toll.direction?.toUpperCase();
  const entryExit = dir === "E" ? { label:"Entry", color:"#16A34A", bg:"#F0FDF4", border:"#BBF7D0" }
                  : dir === "X" ? { label:"Exit",  color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" }
                  : null;

  return (
    <motion.div
      initial={{ opacity:0, y:16, scale:.96 }}
      animate={{ opacity:1, y:0,  scale:1   }}
      exit={{    opacity:0, y:16, scale:.96 }}
      transition={{ duration:0.2, ease:"easeOut" }}
      style={{
        position:"absolute", bottom:80, left:"50%",
        transform:"translateX(-50%)", zIndex:1000,
        width:300, background:"#ffffff",
        border:"1px solid #E5E7EB", borderRadius:12,
        boxShadow:"0 8px 32px rgba(0,0,0,0.12)",
        overflow:"hidden",
      }}
    >
      <div style={{ height:3, background:"#7C3AED" }} />
      <div style={{ padding:"14px 16px" }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{toll.plaza}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background:"none", border:"1px solid #E5E7EB", borderRadius:6,
              width:26, height:26, cursor:"pointer", color:"#9CA3AF",
              display:"flex", alignItems:"center", justifyContent:"center",
            }}
          ><X size={12} /></button>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {ts && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"8px 12px",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Clock size={11} color="#9CA3AF" />
                <span style={{ fontSize:12, color:"#6B7280" }}>Crossed at</span>
              </div>
              <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{ts}</span>
            </div>
          )}
          {toll.lat && toll.lng && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"8px 12px",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <MapPin size={11} color="#9CA3AF" />
                <span style={{ fontSize:12, color:"#6B7280" }}>Coordinates</span>
              </div>
              <span style={{ fontSize:11, color:"#374151", fontFamily:"monospace" }}>
                {toll.lat.toFixed(4)}°, {toll.lng.toFixed(4)}°
              </span>
            </div>
          )}
          {entryExit && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"8px 12px",
            }}>
              <span style={{ fontSize:12, color:"#6B7280" }}>Crossing type</span>
              <span style={{
                fontSize:11, fontWeight:700, color:entryExit.color,
                background:entryExit.bg, border:`1px solid ${entryExit.border}`,
                borderRadius:5, padding:"2px 8px",
              }}>{entryExit.label}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Animated marker ───────────────────────────────────── */
function AnimatedMarker({ vehicle, isSelected, onSelect, onHover }) {
  const markerRef  = useRef(null);
  const rafRef     = useRef(null);
  const mountedRef = useRef(true);
  const prevLatRef = useRef(vehicle.lat);
  const prevLngRef = useRef(vehicle.lng);
  const [initPos]  = useState([vehicle.lat, vehicle.lng]);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const fromLat = prevLatRef.current, fromLng = prevLngRef.current;
    const toLat = vehicle.lat, toLng = vehicle.lng;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const t0 = performance.now(), dur = 2800;
    function step(now) {
      if (!mountedRef.current || !marker._map) {
        prevLatRef.current = toLat; prevLngRef.current = toLng; return;
      }
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      marker.setLatLng([fromLat + (toLat - fromLat) * e, fromLng + (toLng - fromLng) * e]);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else { prevLatRef.current = toLat; prevLngRef.current = toLng; }
    }
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [vehicle.lat, vehicle.lng]); // eslint-disable-line

  useEffect(() => {
    markerRef.current?.setIcon(makeIcon(vehicle, isSelected));
  }, [isSelected, vehicle.status, vehicle.type]); // eslint-disable-line

  return (
    <Marker
      ref={markerRef}
      position={initPos}
      icon={makeIcon(vehicle, isSelected)}
      zIndexOffset={isSelected ? 1000 : 0}
      eventHandlers={{
        click:     () => onSelect(vehicle),
        mouseover: e  => !isSelected && onHover(vehicle, e.originalEvent),
        mouseout:  ()  => onHover(null),
      }}
    />
  );
}

/* ── Hover cards ───────────────────────────────────────── */
function VehicleHoverCard({ vehicle, pos }) {
  return (
    <div style={{
      position:"absolute", left:pos.x, top:pos.y - 72,
      transform:"translateX(-50%)", zIndex:2000, pointerEvents:"none",
      background:"#ffffff", border:"1px solid #E5E7EB",
      borderRadius:8, padding:"8px 12px",
      boxShadow:"0 4px 16px rgba(0,0,0,0.1)", whiteSpace:"nowrap",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
        <span style={{ fontWeight:600, fontSize:13, color:"#111827" }}>{vehicle.nickname}</span>
        {vehicle.isEstimated && (
          <span style={{
            fontSize:9, fontWeight:700, color:"#D97706",
            background:"#FFFBEB", border:"1px solid #FDE68A",
            borderRadius:4, padding:"1px 5px",
          }}>~ Est.</span>
        )}
      </div>
      <div style={{ fontSize:11, color:"#6B7280" }}>
        {vehicle.status} · {vehicle.tollHistory?.length || 0} toll{vehicle.tollHistory?.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

function TollHoverCard({ toll, pos }) {
  const ts = toll.timestamp
    ? new Date(toll.timestamp).toLocaleString("en-IN", {
        day:"numeric", month:"short", hour:"2-digit", minute:"2-digit", hour12:true,
      })
    : null;
  // Only show Entry / Exit — compass directions (N/S/W) are not user-facing
  const dir = toll.direction?.toUpperCase();
  const entryExit = dir === "E" ? { label:"Entry", color:"#16A34A", bg:"#F0FDF4", border:"#BBF7D0" }
                  : dir === "X" ? { label:"Exit",  color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" }
                  : null;

  return (
    <div style={{
      position:"absolute", left:pos.x, top:pos.y - 20,
      transform:"translate(-50%, -100%)", zIndex:2000, pointerEvents:"none",
      width:280, background:"#ffffff",
      border:"1px solid #E5E7EB", borderRadius:12,
      boxShadow:"0 8px 32px rgba(0,0,0,0.14)", overflow:"hidden",
    }}>
      <div style={{ height:3, background:"#7C3AED" }} />
      <div style={{ padding:"12px 14px" }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:8 }}>{toll.plaza}</div>
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          {ts && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"7px 11px",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Clock size={11} color="#9CA3AF" />
                <span style={{ fontSize:12, color:"#6B7280" }}>Crossed at</span>
              </div>
              <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{ts}</span>
            </div>
          )}
          {toll.lat && toll.lng && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"7px 11px",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <MapPin size={11} color="#9CA3AF" />
                <span style={{ fontSize:12, color:"#6B7280" }}>Coordinates</span>
              </div>
              <span style={{ fontSize:11, color:"#374151", fontFamily:"monospace" }}>
                {toll.lat.toFixed(4)}°, {toll.lng.toFixed(4)}°
              </span>
            </div>
          )}
          {entryExit && (
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:"#F9FAFB", border:"1px solid #F3F4F6",
              borderRadius:8, padding:"7px 11px",
            }}>
              <span style={{ fontSize:12, color:"#6B7280" }}>Type</span>
              <span style={{
                fontSize:11, fontWeight:700, color:entryExit.color,
                background:entryExit.bg, border:`1px solid ${entryExit.border}`,
                borderRadius:5, padding:"2px 8px",
              }}>{entryExit.label}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Camera controller ─────────────────────────────────── */
function CameraController({ vehicle, zoomTrigger }) {
  const map = useMap();
  const prevKey = useRef(null);
  useEffect(() => {
    if (!vehicle) return;
    // Key changes on: new vehicle, tracking toggle, route arriving, or explicit zoom trigger
    const hasRoute = vehicle.route?.length > 1 ? "r" : "n";
    const key = `${vehicle.id}_${vehicle.isTracking ? "t" : "v"}_${hasRoute}_${zoomTrigger}`;
    if (key === prevKey.current) return;
    prevKey.current = key;

    const tracking = vehicle.isTracking;
    if (vehicle.route?.length > 1) {
      const bounds = L.latLngBounds(vehicle.route.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, {
        padding:    tracking ? [50, 50] : [80, 80],
        maxZoom:    tracking ? 15 : 13,
        animate:    true,
        duration:   1.3,
      });
    } else if (vehicle.lat && vehicle.lng) {
      map.flyTo([vehicle.lat, vehicle.lng], tracking ? 15 : 13, { duration:1.3 });
    }
  }, [vehicle?.id, vehicle?.isTracking, vehicle?.route?.length, zoomTrigger]); // eslint-disable-line
  return null;
}

function TollFlyTo({ toll }) {
  const map = useMap();
  const prevId = useRef(null);
  useEffect(() => {
    if (!toll || toll.id === prevId.current) return;
    prevId.current = toll.id;
    map.flyTo([toll.lat, toll.lng], Math.max(map.getZoom(), 13), { duration:1.0 });
  }, [toll?.id]); // eslint-disable-line
  return null;
}

/* ── Custom zoom controls ──────────────────────────────── */
function MapControls({ vehicle }) {
  const map = useMap();

  function fitRoute() {
    if (!vehicle) return;
    if (vehicle.route?.length > 1) {
      const bounds = L.latLngBounds(vehicle.route.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding:[50, 50], maxZoom:15, animate:true, duration:1.0 });
    } else if (vehicle.lat && vehicle.lng) {
      map.flyTo([vehicle.lat, vehicle.lng], 15, { duration:1.0 });
    }
  }

  const btnStyle = {
    width:36, height:36, background:"#ffffff",
    border:"1px solid #E5E7EB", borderRadius:8,
    display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", color:"#374151",
    boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
    transition:"all 0.15s",
  };

  return (
    <div style={{
      position:"absolute", right:16, bottom:80, zIndex:900,
      display:"flex", flexDirection:"column", gap:6,
    }}>
      <button style={btnStyle}
        onMouseOver={e => { e.currentTarget.style.background="#EEF2FF"; e.currentTarget.style.borderColor="#BFDBFE"; }}
        onMouseOut={e  => { e.currentTarget.style.background="#ffffff"; e.currentTarget.style.borderColor="#E5E7EB"; }}
        onClick={() => map.zoomIn()}
        title="Zoom in"
      ><Plus size={16} strokeWidth={2.5} /></button>

      <button style={btnStyle}
        onMouseOver={e => { e.currentTarget.style.background="#EEF2FF"; e.currentTarget.style.borderColor="#BFDBFE"; }}
        onMouseOut={e  => { e.currentTarget.style.background="#ffffff"; e.currentTarget.style.borderColor="#E5E7EB"; }}
        onClick={() => map.zoomOut()}
        title="Zoom out"
      ><Minus size={16} strokeWidth={2.5} /></button>

      {vehicle?.route?.length > 0 && (
        <button style={{ ...btnStyle, background:"#2563EB", border:"1px solid #1D4ED8", color:"#fff" }}
          onMouseOver={e => { e.currentTarget.style.background="#1D4ED8"; }}
          onMouseOut={e  => { e.currentTarget.style.background="#2563EB"; }}
          onClick={fitRoute}
          title="Fit route"
        ><Maximize2 size={14} strokeWidth={2.5} /></button>
      )}
    </div>
  );
}

/* ── Route ─────────────────────────────────────────────── */
function DrawnRoute({ route, vehicleId, isLive }) {
  const ref = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => {
      const path = ref.current?._path;
      if (!path) return;
      const BIG = 60000;
      path.style.transition = "none";
      path.style.strokeDasharray  = String(BIG);
      path.style.strokeDashoffset = String(BIG);
      void path.getBoundingClientRect();
      path.style.transition = "stroke-dashoffset 1.8s cubic-bezier(0.35,0,0.2,1)";
      path.style.strokeDashoffset = "0";
    }, 80);
    return () => clearTimeout(id);
  }, [vehicleId]);

  if (!route || route.length < 2) return null;

  const pts = route.map(p => [p.lat, p.lng]);

  return (
    <>
      {/* Outer glow */}
      <Polyline
        positions={pts}
        pathOptions={{ color:"#2563EB", weight:20, opacity:0.07, lineCap:"round", lineJoin:"round" }}
      />
      {/* Mid halo */}
      <Polyline
        positions={pts}
        pathOptions={{ color:"#2563EB", weight:10, opacity:0.15, lineCap:"round", lineJoin:"round" }}
      />
      {/* Main animated line */}
      <Polyline
        ref={ref}
        positions={pts}
        pathOptions={{ color: isLive ? "#2563EB" : "#3B82F6", weight:5, opacity:1, lineCap:"round", lineJoin:"round" }}
      />
      {/* White core — makes it look like a neon road */}
      <Polyline
        positions={pts}
        pathOptions={{ color:"#ffffff", weight:1.5, opacity:0.55, lineCap:"round", lineJoin:"round" }}
      />
    </>
  );
}

/* ── Route info bar (top centre) ───────────────────────── */
function RouteInfoBar({ vehicle }) {
  const lastUpdated = vehicle.lastUpdated
    ? new Date(vehicle.lastUpdated).toLocaleString("en-IN", {
        day:"numeric", month:"short", hour:"2-digit", minute:"2-digit", hour12:true,
      })
    : null;

  const tollCount = vehicle.tollHistory?.length || 0;

  return (
    <motion.div
      initial={{ opacity:0, y:-16 }}
      animate={{ opacity:1, y:0 }}
      exit={{    opacity:0, y:-16 }}
      transition={{ duration:0.22, ease:"easeOut" }}
      style={{
        position:"absolute", top:12, left:"50%", transform:"translateX(-50%)",
        zIndex:900, pointerEvents:"none",
      }}
    >
      <div style={{
        background:"rgba(255,255,255,0.97)",
        border:"1px solid #EBEBEB",
        borderRadius:14, padding:"10px 16px",
        display:"flex", alignItems:"center", gap:0,
        boxShadow:"0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        whiteSpace:"nowrap",
        backdropFilter:"blur(8px)",
      }}>
        {/* Start */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{
            width:9, height:9, borderRadius:"50%", background:"#16A34A",
            boxShadow:"0 0 0 3px rgba(22,163,74,0.2)", flexShrink:0,
          }} />
          <div>
            <div style={{ fontSize:9, color:"#9CA3AF", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Origin</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#111827", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis" }}>
              {vehicle.startLocation || "Journey Start"}
            </div>
          </div>
        </div>

        {/* Toll count pill */}
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"0 14px" }}>
          <div style={{ width:20, height:1.5, background:"#E5E7EB" }} />
          <div style={{
            background: tollCount > 0 ? "#2563EB" : "#F3F4F6",
            color: tollCount > 0 ? "#fff" : "#9CA3AF",
            fontSize:10, fontWeight:700, borderRadius:99,
            padding:"3px 9px", letterSpacing:0.3,
          }}>
            {tollCount} toll{tollCount !== 1 ? "s" : ""}
          </div>
          <div style={{ width:20, height:1.5, background:"#E5E7EB" }} />
        </div>

        {/* Last seen */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div>
            <div style={{ fontSize:9, color:"#9CA3AF", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, textAlign:"right" }}>Last Seen</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#2563EB", textAlign:"right", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis" }}>
              {vehicle.location}
            </div>
            {lastUpdated && (
              <div style={{ fontSize:10, color:"#9CA3AF", textAlign:"right" }}>{lastUpdated}</div>
            )}
          </div>
          <div style={{
            width:9, height:9, borderRadius:"50%", background:"#2563EB",
            boxShadow:"0 0 0 3px rgba(79,70,229,0.2)", flexShrink:0,
          }} />
        </div>
      </div>
    </motion.div>
  );
}

/* ── Legend ────────────────────────────────────────────── */
function Legend() {
  return (
    <div style={{
      position:"absolute", bottom:16, left:16, zIndex:900,
      background:"#ffffff", border:"1px solid #E5E7EB",
      borderRadius:8, padding:"10px 12px",
      boxShadow:"0 1px 6px rgba(0,0,0,0.06)",
    }}>
      <div style={{ fontSize:9, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.6, marginBottom:8 }}>
        Legend
      </div>
      {[
        { color:"#16A34A", label:"Active"  },
        { color:"#D97706", label:"Idle"    },
        { color:"#9CA3AF", label:"Offline" },
      ].map(({ color, label }) => (
        <div key={label} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:color }} />
          <span style={{ fontSize:11, color:"#6B7280" }}>{label}</span>
        </div>
      ))}
      <div style={{ borderTop:"1px solid #F3F4F6", marginTop:5, paddingTop:5 }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
          <div style={{ width:16, height:3, background:"#2563EB", borderRadius:99 }} />
          <span style={{ fontSize:11, color:"#6B7280" }}>FASTag route</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <div style={{ width:14, height:14, borderRadius:"50%", background:"#fff", border:"1.5px solid #7C3AED", display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"#7C3AED", fontWeight:700 }}>₹</div>
          <span style={{ fontSize:11, color:"#6B7280" }}>Toll plaza</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <span style={{ fontSize:14 }}>🏠</span>
          <span style={{ fontSize:11, color:"#6B7280" }}>Registered address</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:4 }}>
          <div style={{ width:14, height:14, borderRadius:4, background:"#FFFBEB", border:"2px dashed #D97706", display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"#D97706" }}>~</div>
          <span style={{ fontSize:11, color:"#6B7280" }}>Registration state</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:4 }}>
          <span style={{ fontSize:9, fontWeight:700, color:"#D97706", background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:3, padding:"1px 4px" }}>~</span>
          <span style={{ fontSize:11, color:"#6B7280" }}>Estimated (between tolls)</span>
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ───────────────────────────────────────── */
function EmptyState() {
  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      transition={{ duration:0.25 }}
      style={{
        position:"absolute", bottom:80, left:"50%", transform:"translateX(-50%)",
        zIndex:900, pointerEvents:"none",
      }}
    >
      <div style={{
        background:"#ffffff", border:"1px solid #E5E7EB",
        borderRadius:10, padding:"12px 20px",
        boxShadow:"0 2px 12px rgba(0,0,0,0.06)",
        display:"flex", alignItems:"center", gap:10,
      }}>
        <Navigation size={14} color="#2563EB" />
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>Select a vehicle to track</div>
          <div style={{ fontSize:11, color:"#9CA3AF" }}>Click any marker or vehicle in the panel</div>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Main export ───────────────────────────────────────── */
export default function MapView({ vehicles, selectedVehicle, onSelectVehicle, focusedToll, onTollClick, estimatedPositions, zoomTrigger }) {
  const [hovVehicle, setHovVehicle] = useState(null);
  const [hovToll,    setHovToll]    = useState(null);
  const [hoverPos,   setHoverPos]   = useState({ x:0, y:0 });
  const containerRef   = useRef(null);
  const tollHoverTimer = useRef(null);

  const sv = selectedVehicle;
  const hasRoute = sv?.route?.length > 1;

  function handleVehicleHover(vehicle, nativeEvent) {
    if (!vehicle) { setHovVehicle(null); return; }
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverPos({ x: nativeEvent.clientX - rect.left, y: nativeEvent.clientY - rect.top });
    setHovVehicle(vehicle);
  }

  function handleTollHover(toll, nativeEvent) {
    if (tollHoverTimer.current) { clearTimeout(tollHoverTimer.current); tollHoverTimer.current = null; }
    if (!toll) {
      // Short delay prevents flicker when cursor briefly leaves the marker hit-area
      tollHoverTimer.current = setTimeout(() => setHovToll(null), 120);
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverPos({ x: nativeEvent.clientX - rect.left, y: nativeEvent.clientY - rect.top });
    setHovToll(toll);
  }

  return (
    <div
      ref={containerRef}
      style={{ position:"absolute", inset:0 }}
      onMouseLeave={() => { setHovToll(null); setHovVehicle(null); }}
    >
      <MapContainer
        center={INDIA_CENTER} zoom={5} minZoom={4} maxZoom={18}
        maxBounds={INDIA_BOUNDS} maxBoundsViscosity={0.8}
        zoomControl={false}
        style={{ width:"100%", height:"100%" }}
      >
        <TileLayer
          url="/api/tiles/{s}/{z}/{x}/{y}?v=2"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          subdomains="abc" maxZoom={18}
        />

        <CameraController vehicle={sv} zoomTrigger={zoomTrigger} />
        <TollFlyTo toll={focusedToll} />
        <MapControls vehicle={sv} />

        {/* Route — always shown when vehicle selected */}
        {sv && hasRoute && (
          <>
            <DrawnRoute route={sv.route} vehicleId={sv.id} isLive={sv.isTracking} />

            {/* Intermediate toll waypoints */}
            {sv.route.slice(1, -1).map((p, i) => (
              <CircleMarker
                key={i} center={[p.lat, p.lng]} radius={7}
                pathOptions={{ color:"#2563EB", fillColor:"#ffffff", fillOpacity:1, weight:2.5, opacity:1 }}
              />
            ))}

            {/* START marker */}
            <Marker
              position={[sv.route[0].lat, sv.route[0].lng]}
              icon={START_ICON}
              zIndexOffset={-100}
            />

            {/* CURRENT / last known location marker */}
            <Marker
              position={[sv.route[sv.route.length - 1].lat, sv.route[sv.route.length - 1].lng]}
              icon={CURRENT_ICON}
              zIndexOffset={-50}
            />
          </>
        )}

        {/* Toll markers — always shown when vehicle selected */}
        {sv && (
          <TollMarkers
            vehicle={sv}
            activeTollId={focusedToll?.id}
            onTollClick={onTollClick}
            onTollHover={handleTollHover}
          />
        )}

        {/* Vehicle markers */}
        {(sv ? [sv] : vehicles).map(v => {
          // Skip if no coordinates or stuck at India-center fallback (old noRecentData default)
          if (!v.lat || !v.lng) return null;
          const indiaCenter = Math.abs(v.lat - 20.5937) < 0.01 && Math.abs(v.lng - 78.9629) < 0.01;
          if (indiaCenter && v.noRecentData && !v.isRCLocation && !v.isStateLocation) return null;

          // RC registered address: static home pin
          if (v.isRCLocation) {
            return (
              <Marker
                key={v.id}
                position={[v.lat, v.lng]}
                icon={makeHomeIcon(sv?.id === v.id)}
                zIndexOffset={sv?.id === v.id ? 1000 : 0}
                eventHandlers={{
                  click:     () => onSelectVehicle(v),
                  mouseover: e  => !sv && handleVehicleHover(v, e.originalEvent),
                  mouseout:  ()  => handleVehicleHover(null),
                }}
              />
            );
          }

          // State-estimate location: dashed amber pin (free geocode from plate)
          if (v.isStateLocation) {
            return (
              <Marker
                key={v.id}
                position={[v.lat, v.lng]}
                icon={makeStateIcon(sv?.id === v.id)}
                zIndexOffset={sv?.id === v.id ? 1000 : 0}
                eventHandlers={{
                  click:     () => onSelectVehicle(v),
                  mouseover: e  => !sv && handleVehicleHover(v, e.originalEvent),
                  mouseout:  ()  => handleVehicleHover(null),
                }}
              />
            );
          }

          // Real FASTag location — merge estimated position so the marker animates between tolls
          const est = estimatedPositions?.[v.id];
          const renderV = est ? { ...v, lat: est.lat, lng: est.lng, isEstimated: true } : v;

          return (
            <AnimatedMarker
              key={v.id} vehicle={renderV}
              isSelected={sv?.id === v.id}
              onSelect={() => onSelectVehicle(v)}
              onHover={handleVehicleHover}
            />
          );
        })}
      </MapContainer>

      {/* Hover cards */}
      {hovVehicle && !sv    && <VehicleHoverCard vehicle={hovVehicle} pos={hoverPos} />}
      {hovToll    && !focusedToll && <TollHoverCard toll={hovToll} pos={hoverPos} />}

      {/* Toll detail card */}
      <AnimatePresence>
        {focusedToll && (
          <TollDetailCard key={focusedToll.id} toll={focusedToll} onClose={() => onTollClick(focusedToll)} />
        )}
      </AnimatePresence>

      {/* Route info bar — start → current */}
      <AnimatePresence>
        {sv && !focusedToll && !sv.isRCLocation && !sv.isStateLocation && <RouteInfoBar key="routebar" vehicle={sv} />}
      </AnimatePresence>

      {/* Empty state */}
      <AnimatePresence>
        {!sv && <EmptyState key="empty" />}
      </AnimatePresence>

      <Legend />
    </div>
  );
}
